import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form"
import { fill as fillPDF, SCHEMA_TO_PDF_FIELD, PAGE_1_SCHEMA_FIELDS, STEP_ANCHOR_FIELDS } from "./fill.js"
import templateUrl from "./template.pdf"
import { PRESETS } from "./presets.js"
import { formSchema, defaultValues } from "./schema.js"
import { yearOptions, frenchOptions } from "./options.js"
import { STEPS, STEP_FIELDS } from "./steps.js"
import { useReportForm } from "@/reports/shared_utils/useReportForm"
import { PillSelector } from "@/reports/report_components/PillSelector"
import { SectionHeading } from "@/reports/report_components/SectionHeading"
import { FormFieldGrid } from "@/reports/report_components/FormFieldGrid"
import { AICommentButton } from "@/reports/report_components/AICommentButton"
import { StepIndicator } from "@/reports/report_components/StepIndicator"
import { ReportFormShell } from "@/reports/report_components/ReportFormShell"
import { KEY_LEARNING_CONFIG } from "./aiConfig"

const FORM_CONFIG = {
  formSchema,
  defaultValues,
  fillPDF,
  templateUrl,
  STEPS,
  STEP_FIELDS,
  STEP_ANCHOR_FIELDS,
  SCHEMA_TO_PDF_FIELD,
  getPageIndex: (f) => (PAGE_1_SCHEMA_FIELDS.has(f) ? 0 : 1),
  getInitialPage: (s) => (s === 4 ? 2 : 1),
  reportTypeKey: 'kindergarten_initial_2016_06',
}

export const KindergartenInitial2016 = ({ reportId, backUrl, contextInfo, defaultsGroupId, onRequestSave, readOnly = false, logos = [], logosVersion = null, isOwner = false, ownSignature = null } = {}) => {
  const hook = useReportForm(FORM_CONFIG, { reportId, defaultsGroupId, groupGrade: contextInfo?.grade, logos, logosVersion, isOwner, ownSignature })

  const renderStep = (form, currentStep) => {
    const { control } = form
    const wrap = (children) => (
      <div className="rounded-2xl border border-border bg-card shadow-sm p-6">{children}</div>
    )

    if (currentStep === 1) {
      return wrap(
        <div className="space-y-5">
          <SectionHeading title="Student Information" />
          <FormFieldGrid
            fields={[
              { name: "studentName", label: "Student Name" },
              { name: "oen",         label: "OEN" },
              { name: "date",        label: "Date" },
            ]}
            control={control}
          />
          <div className="rounded-xl border border-border bg-card p-4 space-y-2">
            <p className="font-semibold text-foreground text-sm">Year</p>
            <FormField
              control={control}
              name="year"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <PillSelector value={field.value} onChange={field.onChange} options={yearOptions} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <SectionHeading title="Attendance" />
          <FormFieldGrid
            fields={[
              { name: "daysAbsent",      label: "Days Absent (this term)", inputMode: "numeric" },
              { name: "totalDaysAbsent", label: "Total Days Absent",       inputMode: "numeric" },
              { name: "timesLate",       label: "Times Late (this term)",  inputMode: "numeric" },
              { name: "totalTimesLate",  label: "Total Times Late",        inputMode: "numeric" },
            ]}
            control={control}
            gridClassName="grid grid-cols-2 gap-6"
          />
        </div>
      )
    }

    if (currentStep === 2) {
      return wrap(
        <div className="space-y-5">
          <SectionHeading title="School Information" />
          <FormFieldGrid
            fields={[
              { name: "teacher",                label: "Teacher" },
              { name: "earlyChildhoodEducator", label: "Early Childhood Educator" },
              { name: "principal",              label: "Principal" },
              { name: "telephone",              label: "Telephone" },
              { name: "board",                  label: "School Board" },
              { name: "school",                 label: "School" },
            ]}
            control={control}
          />
          <FormField
            control={control}
            name="schoolAddress"
            render={({ field }) => (
              <FormItem>
                <FormLabel>School Address</FormLabel>
                <FormControl>
                  <Textarea {...field} className="min-h-[64px]" placeholder="Street&#10;City, Province Postal" />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="boardAddress"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Board Address</FormLabel>
                <FormControl>
                  <Textarea {...field} className="min-h-[64px]" placeholder="Street&#10;City, Province Postal" />
                </FormControl>
              </FormItem>
            )}
          />
          <div className="rounded-xl border border-border bg-card p-4 space-y-2">
            <p className="font-semibold text-foreground text-sm">French Program</p>
            <FormField
              control={control}
              name="frenchProgram"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <PillSelector value={field.value} onChange={field.onChange} options={frenchOptions} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>
      )
    }

    if (currentStep === 3) {
      return wrap(
        <div className="space-y-5">
          <SectionHeading title="Designations" />
          <div className="grid grid-cols-2 gap-4">
            {[
              { name: "esl", label: "ESL — English as a Second Language" },
              { name: "iep", label: "IEP — Individual Education Plan" },
            ].map(({ name, label }) => (
              <FormField
                key={name}
                control={control}
                name={name}
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-3 rounded-xl border border-border bg-card p-4">
                    <FormControl>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border border-input accent-primary cursor-pointer"
                        checked={field.value === 'true'}
                        onChange={(e) => field.onChange(e.target.checked ? 'true' : '')}
                      />
                    </FormControl>
                    <FormLabel className="!mt-0 cursor-pointer">{label}</FormLabel>
                  </FormItem>
                )}
              />
            ))}
          </div>

          <SectionHeading title="Key Learning / Growth in Learning / Next Steps in Learning" />
          <AICommentButton
            config={KEY_LEARNING_CONFIG}
            form={form}
            fieldName="keyLearningComments"
            reportId={reportId}
            label="Comments"
            placeholder="Describe the child's key learning, growth, and next steps based on observations in the play environment."
          />
        </div>
      )
    }

    if (currentStep === 4) {
      return wrap(
        <div className="space-y-5">
          <SectionHeading title="Page 2 — Board Information & Signatures" />
          <p className="text-sm text-muted-foreground">
            These fields appear on the back of the printed report. Most teachers can leave them blank — the school board often fills them out centrally.
          </p>
          <FormField
            control={control}
            name="boardInformation"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Board Information</FormLabel>
                <FormControl>
                  <Textarea {...field} className="min-h-[140px]" placeholder="Board vision statement or educational goals" />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="eceNames"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Early Childhood Educator Names</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Used on the signature line at the bottom of page 2" />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      )
    }

    return null
  }

  return (
    <ReportFormShell
      hook={hook}
      title="Kindergarten Communication of Learning — Initial Observations"
      reportId={reportId}
      backUrl={backUrl}
      contextInfo={contextInfo}
      defaultsGroupId={defaultsGroupId}
      onRequestSave={onRequestSave}
      STEPS={STEPS}
      PRESETS={PRESETS}
      defaultPageCount={2}
      StepIndicator={StepIndicator}
      stepIndicatorProps={{ steps: STEPS }}
      renderStep={renderStep}
      formKey={hook.currentStep}
      readOnly={readOnly}
    />
  )
}
